import { getErrorMessage, readProperty } from '@/lib/utils'

const businessSuffixWords = new Set([
  'co',
  'company',
  'corp',
  'corporation',
  'inc',
  'incorporated',
  'limited',
  'llc',
  'llp',
  'lp',
  'ltd',
  'pllc',
  'the',
])

function normalizeFloristNameForComparison(name: string | null | undefined) {
  return String(name ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\bl\.?\s*l\.?\s*c\.?\b/g, ' llc ')
    .replace(/\binc\.?\b/g, ' inc ')
    .replace(/\bco\.?\b/g, ' co ')
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((word) => word && !businessSuffixWords.has(word))
    .join(' ')
}

export interface FloristNameComparison {
  organizationLabel: string
  fsnClientLabel: string
  /** Identifies the compared pair, so a confirmed mismatch isn't asked about again. */
  key: string
  needsConfirmation: boolean
}

function getFloristNameComparison(
  organizationName: string | null | undefined,
  fsnClientName: string | null | undefined
): FloristNameComparison {
  const organizationLabel = String(organizationName ?? '').trim()
  const fsnClientLabel = String(fsnClientName ?? '').trim()
  const organizationComparableName = normalizeFloristNameForComparison(organizationLabel)
  const fsnComparableName = normalizeFloristNameForComparison(fsnClientLabel)
  const comparable = Boolean(organizationComparableName && fsnComparableName)

  return {
    organizationLabel: organizationLabel || 'Current organization',
    fsnClientLabel: fsnClientLabel || 'Selected FSN client',
    key: `${organizationComparableName}::${fsnComparableName}`,
    needsConfirmation: comparable && organizationComparableName !== fsnComparableName,
  }
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

/** The first Laravel field error, else the error's message, else the fallback. */
function getFsnImportErrorDescription(error: unknown, fallbackMessage: string): string {
  const fieldErrors = readProperty(readProperty(readProperty(error, 'response'), 'data'), 'errors')
  const firstFieldMessage =
    typeof fieldErrors === 'object' && fieldErrors !== null
      ? Object.values(fieldErrors).flat().find(isNonEmptyString)
      : null

  if (firstFieldMessage) {
    return firstFieldMessage
  }

  const message = getErrorMessage(error)

  return typeof message === 'string' && message.trim() !== '' ? message : fallbackMessage
}

export { getFloristNameComparison, getFsnImportErrorDescription }
